import type { CompositionV3BreakdownOptions } from '@revelio/data-access';
import { EntKind } from '@revelio/data-access';

import {
  getEntKindForSkillLevelId,
  skillEntKindFromTier,
} from '../role-skill-ent-kind';
import {
  getTreemapDrilldownIsLeafLevel,
  getTreemapDrilldownLevelOrder,
  isFlatTreemapSegment,
  type RoleLadder,
  type TreemapDrilldownSegment,
} from '../treemapDrilldownLevels';
import type { CompositionV3BreakdownLevelOverrides } from './getCompositionV3BreakdownOptions';

/**
 * Geography selection-list key → the breakdown `EntKind` it requests.
 *
 * Keyed off the active taxonomy's level keys rather than a per-mode constant,
 * so the breakdown level always matches the level the filter tree actually
 * offers — a per-mode constant can't express a fourth level that is `city` on
 * one taxonomy and `county` on another.
 */
const GEO_LEVEL_ID_TO_ENT_KIND: Record<string, EntKind> = {
  region: EntKind.Region,
  country: EntKind.Country,
  msa: EntKind.Msa,
  state: EntKind.State,
  city: EntKind.City,
  county: EntKind.County,
};

const ROLE_LEVEL_ORDER: readonly EntKind[] = [
  EntKind.RoleLvl_1,
  EntKind.RoleLvl_3,
  EntKind.RoleLvl_5,
  EntKind.RoleLvl_7,
];

const ROLE_FINE_LEVEL_ORDER: readonly EntKind[] = [
  EntKind.RoleLvl_1,
  EntKind.RoleLvl_3,
  EntKind.RoleLvl_5,
  EntKind.RoleLvl_6,
  EntKind.RoleLvl_7,
];

const RICS_LEVEL_ORDER: readonly EntKind[] = [
  EntKind.RicsK10,
  EntKind.RicsK50,
  EntKind.RicsK400,
];

const ACTIVITY_LEVEL_ORDER: readonly EntKind[] = [
  EntKind.ActivityLvl_3,
  EntKind.ActivityLvl_5,
  EntKind.ActivityLvl_6,
];

function skillLevelOrder(): readonly EntKind[] {
  return getTreemapDrilldownLevelOrder('skills').map(
    (levelId, index) =>
      getEntKindForSkillLevelId(levelId) ?? skillEntKindFromTier(index + 1)
  );
}

function levelOrderForSegment(
  segment: TreemapDrilldownSegment,
  geographySelectionListIds: readonly string[],
  roleLadder: RoleLadder
): readonly EntKind[] {
  switch (segment) {
    case 'geography':
      return geographySelectionListIds
        .map((levelId) => GEO_LEVEL_ID_TO_ENT_KIND[levelId])
        .filter((entKind): entKind is EntKind => entKind !== undefined);
    case 'role':
      return roleLadder === 'fine' ? ROLE_FINE_LEVEL_ORDER : ROLE_LEVEL_ORDER;
    case 'rics':
      return RICS_LEVEL_ORDER;
    case 'skills':
      return skillLevelOrder();
    case 'activities':
      return ACTIVITY_LEVEL_ORDER;
  }
}

type BreakdownLevelField = keyof Pick<
  CompositionV3BreakdownOptions,
  | 'geo_breakdown_level'
  | 'role_breakdown_level'
  | 'rics_breakdown_level'
  | 'skill_breakdown_level'
  | 'activity_breakdown_level'
>;

const BREAKDOWN_LEVEL_FIELD_BY_SEGMENT: Record<
  TreemapDrilldownSegment,
  BreakdownLevelField
> = {
  geography: 'geo_breakdown_level',
  role: 'role_breakdown_level',
  rics: 'rics_breakdown_level',
  skills: 'skill_breakdown_level',
  activities: 'activity_breakdown_level',
};

export function getBreakdownLevelField(
  segment: TreemapDrilldownSegment
): BreakdownLevelField {
  return BREAKDOWN_LEVEL_FIELD_BY_SEGMENT[segment];
}

export function getBreakdownLevelDepth(
  segment: TreemapDrilldownSegment,
  level: EntKind | null,
  geographySelectionListIds: readonly string[],
  roleLadder: RoleLadder = 'standard'
): number | null {
  const order = levelOrderForSegment(
    segment,
    geographySelectionListIds,
    roleLadder
  );
  const index = level === null ? -1 : order.indexOf(level);
  return index === -1 ? null : index;
}

export function getBreakdownLevelAtDepth(
  segment: TreemapDrilldownSegment,
  depth: number,
  geographySelectionListIds: readonly string[],
  roleLadder: RoleLadder = 'standard'
): EntKind | null {
  const order = levelOrderForSegment(
    segment,
    geographySelectionListIds,
    roleLadder
  );
  return order[depth] ?? null;
}

export type NestedTreemapViewDerivation = {
  /** Depth of the displayed cells, or `null` when the view renders flat. */
  childDepth: number | null;
  breakdownLevelOverrides: CompositionV3BreakdownLevelOverrides | undefined;
  parentEntKind: EntKind | null;
  parentBreakdownLevelOverrides:
    | CompositionV3BreakdownLevelOverrides
    | undefined;
  displayLevelId: string;
  isDisplayLeaf: boolean;
  parentLevelId: string | null;
};

// Group headers sit at the filters' natural breakdown level; displayed cells
// one deeper (falling back to a flat view when the tree has no deeper level).
export function deriveNestedTreemapView({
  segment,
  naturalLevel,
  geographySelectionListIds,
  isRicsCompanyBreakdown,
  nestingEnabled = true,
  roleLadder = 'standard',
}: {
  segment: TreemapDrilldownSegment;
  naturalLevel: EntKind | null;
  geographySelectionListIds: readonly string[];
  isRicsCompanyBreakdown: boolean;
  nestingEnabled?: boolean;
  roleLadder?: RoleLadder;
}): NestedTreemapViewDerivation {
  const levelOrder = getTreemapDrilldownLevelOrder(
    segment,
    geographySelectionListIds,
    roleLadder
  );
  const levelField = getBreakdownLevelField(segment);
  const naturalDepth = getBreakdownLevelDepth(
    segment,
    naturalLevel,
    geographySelectionListIds,
    roleLadder
  );

  const { childDepth, breakdownLevelOverrides } = ((): {
    childDepth: number | null;
    breakdownLevelOverrides: CompositionV3BreakdownLevelOverrides | undefined;
  } => {
    if (
      !nestingEnabled ||
      isFlatTreemapSegment(segment) ||
      naturalDepth === null
    ) {
      return { childDepth: null, breakdownLevelOverrides: undefined };
    }
    const childLevel = getBreakdownLevelAtDepth(
      segment,
      naturalDepth + 1,
      geographySelectionListIds,
      roleLadder
    );
    if (childLevel === null) {
      return {
        childDepth: naturalDepth === 0 ? null : naturalDepth,
        breakdownLevelOverrides: undefined,
      };
    }
    const overrides: CompositionV3BreakdownLevelOverrides = {};
    overrides[levelField] = childLevel;
    return { childDepth: naturalDepth + 1, breakdownLevelOverrides: overrides };
  })();

  // Outside the ladder there is no level to name, and depth 0 would let a click
  // drill from a level the data isn't at — render a non-drillable view instead.
  const isUnknownNaturalLevel =
    !isRicsCompanyBreakdown && naturalDepth === null;
  const displayDepth = childDepth ?? naturalDepth ?? 0;
  const displayLevelId = (() => {
    if (isRicsCompanyBreakdown) return 'company';
    if (isUnknownNaturalLevel) return '';
    return levelOrder[displayDepth] ?? levelOrder[levelOrder.length - 1] ?? '';
  })();
  const isDisplayLeaf =
    isRicsCompanyBreakdown ||
    isUnknownNaturalLevel ||
    getTreemapDrilldownIsLeafLevel(
      segment,
      displayLevelId,
      geographySelectionListIds,
      roleLadder
    );
  const parentLevelId =
    childDepth !== null && childDepth >= 1
      ? (levelOrder[childDepth - 1] ?? null)
      : null;

  const parentEntKind =
    childDepth !== null && childDepth >= 1
      ? getBreakdownLevelAtDepth(
          segment,
          childDepth - 1,
          geographySelectionListIds,
          roleLadder
        )
      : null;
  const parentBreakdownLevelOverrides = (():
    | CompositionV3BreakdownLevelOverrides
    | undefined => {
    if (parentEntKind === null) return undefined;
    const overrides: CompositionV3BreakdownLevelOverrides = {};
    overrides[levelField] = parentEntKind;
    return overrides;
  })();

  return {
    childDepth,
    breakdownLevelOverrides,
    parentEntKind,
    parentBreakdownLevelOverrides,
    displayLevelId,
    isDisplayLeaf,
    parentLevelId,
  };
}
